import {Component, EventEmitter, Inject, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {EnvironmentService} from '../../../../common/services/environment-service';
import {detailsExpand} from '../../../../common';

enum Controls {
  OverrideIndicator1 = 'lineOverrideIndicator1',
  OverrideIndicator2 = 'lineOverrideIndicator2',
  OverrideIndicator3 = 'lineOverrideIndicator3',
  PayAsClaimedOverride = 'lineOverrideIndicator4',
  ManualCopaymentOverride = 'manualCopaymentOverride',
  ManualCopaymentPercentageOverride = 'manualCopaymentPercentageOverride',
}

interface DialogData {
  [key: string]: boolean | number | null;

  isAllowedToEdit: boolean;
}

@Component({
  selector: 'app-activity-history-dialog',
  templateUrl: './activity-override-dialog.component.html',
  styleUrls: ['../../../../../../styles/global/_dialog-styles.scss'],
  animations: [detailsExpand]
})
export class ActivityOverrideDialogComponent {
  readonly controls = Controls;
  readonly overrideIndicatorNames: Readonly<{ [key: string]: string }> = {};
  readonly manualCopaymentPercentageOverrideValidators = [Validators.required, Validators.min(0), Validators.max(100), Validators.pattern(/^\d+$/)];
  formGroup: FormGroup;
  heading = 'Activity Override Indicators';

  @Output() updateIndicatorsStatus: EventEmitter<any> = new EventEmitter<any>();

  private readonly isAllowedToEdit: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private environmentService: EnvironmentService,
    public dialogRef: MatDialogRef<ActivityOverrideDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.overrideIndicatorNames = this.environmentService.getActivityOverrideIndicatorNames();
    this.isAllowedToEdit = this.data?.isAllowedToEdit !== undefined && !!this.data?.isAllowedToEdit;

    const isPayAsClaimedOverrideActive = !!this.data[Controls.PayAsClaimedOverride];
    const manualCopaymentPercentageOverride = this.data[Controls.ManualCopaymentPercentageOverride] as number;
    const isManualCopaymentOverrideActive = manualCopaymentPercentageOverride !== null && manualCopaymentPercentageOverride !== undefined
      && manualCopaymentPercentageOverride > 0;

    this.formGroup = this.formBuilder.group({
      [Controls.OverrideIndicator1]: new FormControl({
        value: this.data[Controls.OverrideIndicator1],
        disabled: !this.isAllowedToEdit
      }),
      [Controls.OverrideIndicator2]: new FormControl({
        value: this.data[Controls.OverrideIndicator2],
        disabled: !this.isAllowedToEdit
      }),
      [Controls.OverrideIndicator3]: new FormControl({
        value: this.data[Controls.OverrideIndicator3],
        disabled: !this.isAllowedToEdit
      }),
      [Controls.PayAsClaimedOverride]: new FormControl({
        value: isPayAsClaimedOverrideActive,
        disabled: !this.isAllowedToEdit
      }),
      [Controls.ManualCopaymentOverride]: new FormControl({
        value: isManualCopaymentOverrideActive,
        disabled: !this.isAllowedToEdit
      }),
      [Controls.ManualCopaymentPercentageOverride]: new FormControl({
        value: manualCopaymentPercentageOverride,
        disabled: !this.isAllowedToEdit || !isManualCopaymentOverrideActive
      }, this.manualCopaymentPercentageOverrideValidators),
    });

    this.formGroup.get(Controls.PayAsClaimedOverride).valueChanges.subscribe((checked: boolean): void => {
      if (checked) {
        this.formGroup.get(Controls.ManualCopaymentOverride).setValue(false, {emitEvent: false});
        this.disableManualCopaymentPercentageOverride();
      }
    });

    this.formGroup.get(Controls.ManualCopaymentOverride).valueChanges.subscribe((checked: boolean): void => {
      if (checked) {
        this.formGroup.get(Controls.PayAsClaimedOverride).setValue(false, {emitEvent: false});
        if (this.isAllowedToEdit) {
          this.formGroup.get(Controls.ManualCopaymentPercentageOverride).enable({emitEvent: false});
        }
      }
      else {
        this.disableManualCopaymentPercentageOverride();
      }
    });
  }

  onSubmitClick($event: MatSlideToggleChange): void {
    const isManualCopaymentOverride = this.formGroup.get(Controls.ManualCopaymentOverride).value;
    const isPayAsClaimedOverride = this.formGroup.get(Controls.PayAsClaimedOverride).value;

    this.dialogRef.close({
      [Controls.OverrideIndicator1]: this.formGroup.get(Controls.OverrideIndicator1).value,
      [Controls.OverrideIndicator2]: this.formGroup.get(Controls.OverrideIndicator2).value,
      [Controls.OverrideIndicator3]: this.formGroup.get(Controls.OverrideIndicator3).value,
      [Controls.PayAsClaimedOverride]: isPayAsClaimedOverride,
      [Controls.ManualCopaymentPercentageOverride]: isManualCopaymentOverride
        ? this.formGroup.get(Controls.ManualCopaymentPercentageOverride).value
        : null,
    });
  }

  onCancelDialogClick(): void {
    this.dialogRef.close(null);
  }

  private disableManualCopaymentPercentageOverride(): void {
    this.formGroup.get(Controls.ManualCopaymentPercentageOverride).setValue(null, {emitEvent: false});
    this.formGroup.get(Controls.ManualCopaymentPercentageOverride).disable({emitEvent: false});
  }
}
